import { useRouter } from 'next/router';
import React, { ComponentProps, useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import cx from 'classnames';
import * as actions from '@actions';
import * as actionTypes from '@actionTypes';
import {
  inlineResponseImages,
  removeEmbeddedImages,
  saveSurveyToS3,
  uploadEmbeddedImagesToS3,
  uploadImagesWithCropToS3,
  getSurveyImageFromS3
} from '@lib/saveSurveyToS3';
import { uploadFileToS3 } from '@helpers/s3';
import { CustomSurvey, SurveyResponse } from 'vl-common/src/components/CustomSurvey';
import PleaseLogIn from '@components/Modal/PleaseLogIn';
import useIsRestricted from '@hooks/useIsRestricted';
import { Survey } from 'vl-common/src/schemas/shared';
import useUser from '@vl-core/hooks/useUser';
import { css } from '@emotion/react';
import useTodoActions from '@hooks/useTodoActions';
import { ConfirmationModal } from 'vl-common/src/components/Modals/CustomModals/Confirmation';
import { errorModal } from 'vl-common/src/components/Modals/CustomModals/Error';
import { ToDoItem } from '@components/Patients/ToDos/ToDos';
import toast from 'react-hot-toast';
import { Switch } from 'antd';
import format from 'vl-common/src/lib/formatDate';
import { useTranslation } from 'react-i18next';
import useRandoxSurveyLabTests from 'vl-common/src/hooks/useRandoxSurveyLabTests';
import useCreateRandoxOrderMutation from 'vl-common/src/hooks/useCreateRandoxOrderMutations';
import useApi from 'vl-common/src/hooks/useApi';
import useBookingFlowControl from 'vl-common/src/hooks/useBookingFlowControl';

type SurveyViewType = 'questions' | 'table';

interface AssessmentProps {
  case_review?: boolean;
  className?: string;
  onData?: ComponentProps<typeof CustomSurvey>['onData'];
  pageType: 'clinician' | 'patients';
  refetch?: () => void;
  isLoadingSurveyRes: boolean;
  surveyRes: Survey;
  onSuccess?: () => void;
  readonly?: boolean;
  clearCacheOnSuccess?: boolean;
  apptCompleting?: boolean;
  showSurveyViewToggle?: boolean;
  surveyAnswersOnComplete?: (surveyAnswers: SurveyResponse) => void;
  auditReason?: string;
  auditText?: string;
  showCookieSettings?: () => void;
}

const Assessment = ({
  case_review,
  className,
  onData,
  pageType,
  refetch = () => {},
  isLoadingSurveyRes,
  surveyRes,
  onSuccess,
  readonly,
  clearCacheOnSuccess,
  apptCompleting = false,
  showSurveyViewToggle = false,
  surveyAnswersOnComplete,
  auditReason,
  auditText,
  showCookieSettings = () => {}
}: AssessmentProps) => {
  const { Api } = useApi();
  const { t } = useTranslation('common');
  const { initializeNewAppointmentBooking, initializeF2FBooking } = useBookingFlowControl();
  const { surveyjs_def_id, surveyjs_id, patient_user_guid, case_id, response, shortname } = surveyRes;
  // We want to re-trigger useQuery on survey response change to keep table view up to date
  // If we don't show the toggle, don't have this as a query key to prevent reloading for patients
  const responseQueryKey = showSurveyViewToggle ? response : null;
  const user = useUser();
  const patientName = `${user.first_name} ${user.last_name}`;
  const isRestricted = useIsRestricted();
  const dispatch = useDispatch();
  const [modalVisible, setModalVisible] = useState(false);
  const [nextSurveyModalVisible, setNextSurveyModalVisible] = useState(false);
  const [signInModalVisible, setSignInModalVisible] = useState(false);
  const placeholder = { rawSurveyDefinition: {}, surveyResponse: surveyRes };
  const [surveyView, setSurveyView] = useState<SurveyViewType>(
    showSurveyViewToggle && surveyRes.completed_flag ? 'table' : 'questions'
  );
  const router = useRouter();

  const isAuditSurvey = shortname === 'Audit Survey';
  const readOnly = isAuditSurvey ? false : readonly;

  // This is subtle code, sorry.
  // I'm not sure that we want to *cache* the newSurveyDefinitions below, and I imagine this was the original
  // motivation for using useQuery (VLAXA-1654). The problem is that the newSurveyDefinitions changes after the
  // async actions.getExpandedAssessmentSurveyDef() completes. It starts with a sensible placeholder value, but
  // if this is passed down into the CustomSurvey component then this placeholder gets "snapped in" to its
  // updatedSurveyRes state variable. This is the state variable that CustomSurvey uses to hold the modified
  // state of the survey answers. Problems arose because "late" changes in newSurveyDefinitions read in from the
  // back end were ignored by CustomSurvey. This caused problems in VLNHS-2996.
  // Without doing a major refactor of these two components, I have introduced a randomKey which is unique for
  // each instance of Assessment. I then use the isFetched flag to determine when the latest data has been read
  // from the back end. Only after this has been fetched do I render the CustomSurvey. This avoids the placeholder
  // from getting snapped into the CustomSurvey updatedSurveyRes too early.
  // It seems to me that there's something badly wrong with CustomSurvey (with its 29 props and 11 state variables).
  // Until we can thoughtfully refactor it this is a subtle but pragmatic solution.
  const randomKey = useMemo(() => crypto.randomUUID(), []);

  const queryClient = useQueryClient();

  const removeSurveyDefinitions = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['getExpandedAssessmentSurveyDef']
    });
  }, [queryClient]);

  const auditApptId = isAuditSurvey && surveyRes?.appt_id ? String(surveyRes.appt_id) : undefined;

  const {
    data: newSurveyDefinitions = placeholder,
    status: surveyDefinitionsStatus,
    isFetched,
    error: surveyDefinitionsError,
    refetch: refetchSurveyDefinitions
  } = useQuery({
    queryKey: [
      'getExpandedAssessmentSurveyDef',
      surveyjs_def_id,
      surveyjs_id,
      patient_user_guid,
      pageType,
      case_id,
      randomKey,
      responseQueryKey,
      auditApptId
    ],

    queryFn: async () => {
      const newSurveyDefinitions = await dispatch(
        actions.getExpandedAssessmentSurveyDef(
          surveyjs_def_id,
          surveyjs_id,
          patient_user_guid,
          pageType,
          case_id,
          auditApptId
        )
      );

      const rawSurveyDefinition = newSurveyDefinitions?.surveyDefinition?.surveyjs_defs[0];

      // Ensure we have a valid survey definition with survey_json
      if (!rawSurveyDefinition?.survey_json) {
        throw new Error('Survey definition not found or invalid');
      }

      return {
        rawSurveyDefinition,
        surveyResponse: newSurveyDefinitions?.surveyResponse?.user_surveyjs[0] || surveyRes
      };
    },
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: Infinity
  });

  const { data: users } = useQuery({
    queryKey: ['getPatientDetails', user?.user_guid, surveyRes.patient_user_guid],

    queryFn: async () => {
      const { users } = await dispatch(actions.getPatientDetails(user?.user_guid, surveyRes.patient_user_guid));
      return users;
    }
  });
  const { data: nextSurveys = [] } = useQuery({
    queryKey: ['getNextSurvey', case_id, surveyRes?.completed_flag, surveyRes?.surveyjs_id],
    queryFn: async () => {
      if (surveyRes.case_id && pageType !== 'clinician') {
        const res = await dispatch(actions.getNextSurvey(surveyRes.case_id));
        // getNextSurvey will still have the survey the user just completed
        const nextSurveys = res?.todo?.filter((survey: ToDoItem) => survey?.survey_id !== surveyjs_id);
        return nextSurveys || [];
      }
      return [];
    }
  });

  const todoActions = useTodoActions(nextSurveys);

  const bookAppointment = useCallback(
    (caseId: number, specCatCode: string, areaCode: string, clinicianTypeCode: string) => {
      // clear pre-cached survey so it will have updated complete status
      if (clearCacheOnSuccess && refetch) {
        refetch();
        removeSurveyDefinitions();
      }
      initializeNewAppointmentBooking({
        caseId,
        patientId: user?.user_guid,
        apptType: 'NEW',
        clinType: clinicianTypeCode,
        redirectRoute: '/patients'
      });
      router.push('/patients/choose');
    },
    [patientName, user?.user_guid]
  );

  const bookFaceToFace = useCallback(
    (caseId: number, clinicianTypeCode: string, appointmentId: number) => {
      if (clearCacheOnSuccess && refetch) {
        refetch();
        removeSurveyDefinitions();
      }
      initializeF2FBooking({
        caseId,
        patientId: user?.user_guid,
        previousApptId: appointmentId,
        apptType: 'NEW',
        clinType: clinicianTypeCode
      });
      router.push('/patients/face2face');
    },
    [patientName, user?.user_guid]
  );

  // getExpandedAnswers is called in CustomSurvey/index.tsx when first setting up the survey (attaching event handlers e.g onValueChanged)
  // But only ifs there are previous answers (i.e patient started survey, then left & came back, or refreshed)
  // Or if survey has been completed
  // In this case we don't want to replace the files (so send skipReplacement as true)
  const getExpandedAnswers = useCallback(
    async (answers: any, surveyId: number) => {
      const owner_guid = surveyRes.patient_user_guid || user?.user_guid;
      const expandedAnswers = await inlineResponseImages(answers, owner_guid, !isRestricted, surveyId, dispatch, true);

      // all images are now stored in data URLs
      return expandedAnswers;
    },
    [surveyRes.patient_user_guid || user?.user_guid, isRestricted]
  );

  const consecutiveSaveAnswersErrorRef = useRef(0);
  const consecutiveUninsuredErrorRef = useRef(false);
  const MAX_CONSECUTIVE_ERROR_COUNT = 3;

  const saveSurvey = useCallback(
    async (assessmentSurveyDetails: Parameters<typeof actions.postAssessmentSurvey>[0]) => {
      if ((!surveyRes.clin_editable && pageType === 'clinician') || apptCompleting) return;

      const response = await dispatch(actions.postAssessmentSurvey(assessmentSurveyDetails), false);
      if (response.status === 406 && !consecutiveUninsuredErrorRef.current) {
        consecutiveUninsuredErrorRef.current = true;
        errorModal({
          title: 'An error occurred',
          content: response.statusText || 'An error occurred. Please return to the dashboard.'
        });
        return;
      }

      // If error happens multiple consecutive times we should warn patient about it
      if (response.type === actionTypes.ACTION_FAIL) {
        consecutiveSaveAnswersErrorRef.current++;

        if (consecutiveSaveAnswersErrorRef.current >= MAX_CONSECUTIVE_ERROR_COUNT) {
          errorModal({
            title: 'An error occurred',
            content: response?.error?.message || 'Unable to save your answers'
          });
        }
        return;
      }

      consecutiveSaveAnswersErrorRef.current = 0;
    },
    [surveyRes.clin_editable, pageType]
  );

  const surveyLabTestOrder = useRandoxSurveyLabTests({
    apptId: surveyRes.appt_id,
    surveyEventCode: surveyRes.event_code
  });
  const createRandoxOrder = useCreateRandoxOrderMutation();

  const createTask = useCallback(
    async (
      question: any,
      user_guid: string,
      caseId: number,
      task_type_value: string | null,
      due_date_value: string,
      appointmentId: number,
      surveyjsId: number,
      queue: string
    ) => {
      const { taskNotes, survey } = question;
      const replaceBracedPlaceholders = (input: string) => {
        let output = '';
        let cursor = 0;
        while (cursor < input.length) {
          const open = input.indexOf('{', cursor);
          if (open === -1) {
            output += input.slice(cursor);
            break;
          }
          output += input.slice(cursor, open);
          const close = input.indexOf('}', open + 1);
          if (close === -1) {
            output += input.slice(open);
            break;
          }
          const token = input.slice(open + 1, close);
          const value = JSON.stringify(survey.getQuestionByValueName(token)?.value, undefined, 2);
          output += value ?? `{${token}}`;
          cursor = close + 1;
        }
        return output;
      };
      if (typeof taskNotes === 'string') {
        question.taskNotes = replaceBracedPlaceholders(taskNotes);
      }
      await dispatch(
        actions.taskCreate({
          user_guid,
          patient_guid: user_guid,
          case_id: caseId,
          task_type_code: task_type_value,
          description: question.taskNotes,
          affected_user_guid: '',
          assignee_user_guid: '',
          due_date: due_date_value,
          appointmentId,
          survey_id: surveyjsId,
          hbs_task: queue
        })
      );
    },
    []
  );

  const submitCaseTriage = useCallback(
    async (
      outcome: string,
      user_guid: string,
      case_id: number,
      spec_area_code: string,
      clin_subtype: string,
      discharge_code?: string
    ) => {
      const response = await dispatch(
        actions.caseTriage({
          outcome,
          user_guid,
          case_id,
          spec_area_code,
          clin_subtype,
          discharge_code
        }),
        false // Disable automatic error notifications
      );

      if (response.type === actionTypes.CASE_TRIAGE_FAIL || response.error) {
        let errorMessage = 'Unknown error occurred';

        if (response.error?.json) {
          try {
            const errorData = await response.error.json();
            errorMessage = errorData.message || errorMessage;
          } catch {
            errorMessage = response.error.statusText || errorMessage;
          }
        } else if (response.error && typeof response.error === 'object') {
          errorMessage = response.error.message || errorMessage;
        }

        if (response?.error?.status === 451) {
          if (consecutiveUninsuredErrorRef.current) {
            return response;
          }
          consecutiveUninsuredErrorRef.current = true;
        }

        errorModal({
          title: 'Error',
          content: errorMessage
        });
      }
      return response;
    },
    [dispatch]
  );

  const routeBackToDashboard = useCallback(() => {
    if (clearCacheOnSuccess && refetch) {
      refetch();
      removeSurveyDefinitions();
    }

    if (surveyRes.cat_code === 'LABTEST') {
      const randoxSurveyTests = ['SURVEYJS|LABTEST|LABP', 'SURVEYJS|LABTEST|LABH'];
      const extraLabTestSurveyTodo = todoActions.find((action) => randoxSurveyTests.includes(action.test_id || ''));

      if (extraLabTestSurveyTodo) {
        extraLabTestSurveyTodo?.action?.();
        return;
      }
    }

    if (surveyRes.cat_code === 'INVREQ') {
      onSuccess?.();
      return;
    }

    if (pageType === 'clinician') {
      refetch();
      return;
    }
    if (isRestricted) {
      setSignInModalVisible(true);
    }
    if (todoActions[0]) {
      setNextSurveyModalVisible(true);
    } else {
      router.push('/patients').then();
    }
  }, [surveyRes.cat_code, pageType, isRestricted, todoActions]);

  const getCaseUrgency = useCallback(async () => {
    if (case_id) {
      const caseInformation = await dispatch(actions.getCase(case_id));
      if (caseInformation?.cases?.length > 0) {
        const urgencyCode = caseInformation.cases[0].urg_code;
        return urgencyCode;
      }
    }
    return Promise.resolve('');
  }, [case_id]);

  const updateUrgency = useCallback(async (user_guid: string, caseId: number, urg_code: string) => {
    await dispatch(actions.updateUrgency(user_guid, caseId, urg_code));
  }, []);

  const getUsers = useCallback(async () => users, [users]);

  const { mutateAsync: setCasePathway } = useMutation({
    mutationFn: async (pathway_code: Parameters<typeof actions.setCasePathway>[0]['pathway_code']) => {
      if (!case_id) throw new Error('case_id is required');

      const response = await dispatch(
        actions.setCasePathway({ case_id, user_guid: user?.user_guid, pathway_code }),
        false
      );

      if (response.type === actionTypes.SET_CASE_PATHWAY_SUCCESS) {
        refetch();
        return response;
      }

      const errorResponse = response.error instanceof Response ? response.error : new Response();
      const errorResponseBody = await errorResponse.json();

      const errorMessage =
        'message' in errorResponseBody && errorResponseBody.message !== 'Bad Request'
          ? errorResponseBody.message
          : 'Failed to set case pathway';

      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  });

  const { mutateAsync: changeRandoxOrderStatus } = useMutation({
    mutationFn: async ({
      user_guid,
      appt_id,
      order_route_code,
      status
    }: Parameters<typeof Api.labs.changeOrderStatus>[0]) => {
      try {
        const response = await Api.labs.changeOrderStatus({ user_guid, appt_id, order_route_code, status });

        return response;
      } catch (error) {
        // @ts-expect-error
        const errorMessage = error?.response?.data?.message || 'Failed to change randox order status';
        errorModal({
          title: 'Error',
          content: errorMessage
        });
        throw new Error(errorMessage);
      }
    }
  });

  const showUpdatedDate =
    newSurveyDefinitions.surveyResponse.shortname === 'General Health' ||
    !newSurveyDefinitions.surveyResponse.completed_date;

  return (
    <div
      className={cx('assessment', className)}
      data-testid="assessment-component"
      css={css`
        padding: 0 0 4rem 0;

        .status {
          padding: 0 1em;
        }

        .reason {
          padding-left: 1em;
          font-weight: bold;
        }

        .view-toggle {
          display: flex;
          gap: 1em;
          margin: 1em;

          .active {
            font-weight: bold;
          }
        }
      `}
    >
      {surveyDefinitionsStatus === 'success' && isFetched && auditReason && isAuditSurvey && (
        <div>
          <span className="reason" data-testid="audit-reason">
            {t('Reason for audit:')}{' '}
          </span>{' '}
          {auditReason}
        </div>
      )}
      {surveyDefinitionsStatus === 'success' && isFetched && auditText && isAuditSurvey && (
        <div>
          <span className="reason" data-testid="audit-reason-desc">
            {t('Audit Description:')}{' '}
          </span>
          {auditText}
        </div>
      )}
      {surveyDefinitionsStatus === 'success' && isFetched && (
        <div className="status" data-testid="survey-status">
          <div>{newSurveyDefinitions.surveyResponse.completed_flag ? t('Status: Submitted') : t('Status: Draft')}</div>

          {newSurveyDefinitions.surveyResponse.completed_date && (
            <div className="completed-date" data-testid="survey-completed-date">
              <strong>
                {t('Survey Completed :')}{' '}
                {format(new Date(newSurveyDefinitions.surveyResponse.completed_date), 'dd/MM/yy (kk:mm)')}
              </strong>
            </div>
          )}

          {newSurveyDefinitions.surveyResponse.updated_date && showUpdatedDate && (
            <div className="updated-date" data-testid="survey-updated-date">
              <strong>
                {t('Survey Updated :')}{' '}
                {format(new Date(newSurveyDefinitions.surveyResponse.updated_date), 'dd/MM/yy (kk:mm)')}
              </strong>
            </div>
          )}
        </div>
      )}

      {surveyDefinitionsStatus === 'success' && isFetched && showSurveyViewToggle && (
        <div className="view-toggle">
          <span className={surveyView === 'questions' ? 'active' : ''}>{t('Question Set')}</span>
          <Switch
            defaultChecked={surveyView === 'table'}
            onChange={(checked) => {
              setSurveyView(checked ? 'table' : 'questions');
            }}
          />
          <span className={surveyView === 'table' ? 'active' : ''}>{t('Table')}</span>
        </div>
      )}

      {surveyDefinitionsStatus === 'pending' && <div style={{ padding: '2rem', textAlign: 'center' }} />}

      {surveyDefinitionsStatus === 'error' && (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'red' }}>
          {t('Failed to load survey: ')}
          {surveyDefinitionsError?.message || 'Unknown error'}
          <br />
          <button data-testid="retry-survey-definition" type="button" onClick={() => refetchSurveyDefinitions()}>
            {t('Retry')}
          </button>
        </div>
      )}

      {surveyDefinitionsStatus === 'success' && isFetched && (
        <CustomSurvey
          bookAppointment={bookAppointment}
          bookFaceToFace={bookFaceToFace}
          caseReview={case_review}
          modalVisible={modalVisible}
          createTask={createTask}
          dispatch={dispatch}
          getExpandedAnswers={getExpandedAnswers}
          getUsers={getUsers}
          onData={onData}
          pageType={pageType}
          rawSurveyDefinition={newSurveyDefinitions.rawSurveyDefinition}
          readonly={readOnly}
          removeEmbeddedImages={removeEmbeddedImages}
          routeBackToDashboard={routeBackToDashboard}
          saveSurvey={saveSurvey}
          saveSurveyToS3={saveSurveyToS3}
          setModalVisible={setModalVisible}
          submitCaseTriage={submitCaseTriage}
          setCasePathway={setCasePathway}
          surveyRes={newSurveyDefinitions.surveyResponse}
          uploadEmbeddedImagesToS3={uploadEmbeddedImagesToS3}
          uploadImagesWithCropToS3={uploadImagesWithCropToS3}
          getSurveyImageFromS3={getSurveyImageFromS3}
          uploadFileToS3={uploadFileToS3}
          user={user}
          userIsRestricted={isRestricted}
          updateUrgency={updateUrgency}
          getCaseUrgency={getCaseUrgency}
          isLoadingSurveyData={isLoadingSurveyRes}
          isCompleted={newSurveyDefinitions?.surveyResponse?.completed_flag}
          hasReadPermissions={!isRestricted}
          inTableView={surveyView === 'table'}
          confirmationModalTitle={t('Thank you for answering these questions. Are you happy to send your answers?')}
          surveyLabTestOrder={surveyLabTestOrder}
          createRandoxOrder={createRandoxOrder}
          changeRandoxOrderStatus={changeRandoxOrderStatus}
          surveyAnswersOnComplete={surveyAnswersOnComplete}
          showCookieSettings={showCookieSettings}
        />
      )}
      <ConfirmationModal
        title="Next survey available"
        open={nextSurveyModalVisible}
        onOk={() => {
          if (todoActions[0].action) {
            todoActions[0].action();
            setNextSurveyModalVisible(false);
          } else {
            router.push('/patients').then();
          }
        }}
        onCancel={() => router.push('/patients').then()}
        okText="Continue"
        okButtonProps={{ 'data-testid': 'next-survey-continue' }}
        cancelButtonProps={{ 'data-testid': 'next-survey-return-dashboard' }}
        cancelText="Return to dashboard"
      >
        {t('Please continue to complete the next survey.')}
      </ConfirmationModal>
      <PleaseLogIn
        open={signInModalVisible}
        onCancel={() => {
          setSignInModalVisible(false);
          router.push('/patients');
        }}
        customText={
          user?.auth_type === 'RESTRICTED'
            ? `Thank you for submitting your questionnaire. Please now upgrade your account using one of the methods below.
               You will need to do this to access your consultants plan and recommendations.`
            : 'Thank you for submitting the survey. Would you like to login to access our full range of features?'
        }
      />
    </div>
  );
};

Assessment.displayName = 'Assessment';

export default Assessment;
